import React, { useState, useEffect } from 'react';
import { typesService, ValidTypeOptions } from '../../services/typesService';

interface TypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  includeEmpty?: boolean;
  ariaLabel?: string;
}

export const TypeSelector: React.FC<TypeSelectorProps> = ({
  value,
  onChange,
  id,
  className = '',
  placeholder = 'Select type...',
  disabled = false,
  includeEmpty = true,
  ariaLabel = 'Type'
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const validOptions: ValidTypeOptions = await typesService.getValidOptions();
        setOptions(validOptions.hardware_types);
      } catch (err) {
        console.error('Failed to fetch type options:', err);
        setOptions(['UNKN', 'PGA', 'P4GU', 'IOTL', 'TMS2']);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  if (loading) {
    return (
      <select id={id} className={className} disabled aria-label={ariaLabel}>
        <option>Loading...</option>
      </select>
    );
  }

  return (
    <select
      id={id}
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {includeEmpty && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

interface TypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  allLabel?: string;
  className?: string;
  ariaLabel?: string;
  /**
   * When true, the filter dropdown lists codes of soft-deleted
   * hardware/config types as well, so the options can match rows the
   * parent table is showing under its own `include_inactive` view.
   * Default false → active-only.
   */
  includeInactive?: boolean;
}

export const TypeFilter: React.FC<TypeFilterProps> = ({
  value,
  onChange,
  allLabel = 'All Types',
  className = '',
  ariaLabel = 'Filter by type',
  includeInactive = false,
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const validOptions: ValidTypeOptions = await typesService.getValidOptions(includeInactive);
        if (!cancelled) setOptions(validOptions.hardware_types);
      } catch (err) {
        console.error('Failed to fetch type options:', err);
        if (!cancelled) setOptions(['UNKN', 'PGA', 'P4GU', 'IOTL', 'TMS2']);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [includeInactive]);

  if (loading) {
    return (
      <select className={className} disabled aria-label={ariaLabel}>
        <option>Loading...</option>
      </select>
    );
  }

  return (
    <select
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

export default TypeSelector;
